import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useProducts } from '../../context/ProductContext';
import { useProductCategories } from '../../context/ProductCategoryContext';
import { useEquipmentManufacturers } from '../../context/EquipmentManufacturerContext';
import { useEquipmentTypes } from '../../context/EquipmentTypeContext';
import { useVendors } from '../../context/VendorContext';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Breadcrumb from '../common/Breadcrumb';
import { useRoutePath } from '../../utils/getRoutePath';
import { useLoading } from '../../context/LoadingContext';
import { 
  ArrowLeftIcon,
  ArrowRightIcon,
  CogIcon
} from '@heroicons/react/24/outline';
import Swal from 'sweetalert2';

// Import tab components
import {
  ProductDetailsTab,
  PricingInventoryTab,
  CommissionSettingsTab,
  VendorInformationTab,
  MediaRelatedTab
} from './tabs';

const productSchema = yup.object().shape({
  name: yup.string().required('Product name is required'),
  product_category_id: yup.string().required('Product category is required'),
  description: yup.string(),
  active: yup.boolean(),
  is_equipment_item: yup.boolean(),
  model: yup.string(),
  partNumber: yup.string(),
  sku: yup.string(),
  upc: yup.string(),
  type: yup.string(),
  is_extended_warranty: yup.boolean(),
  warranty_provider_id: yup.string(),
  warranty_expires: yup.string(),
  reorder_point: yup.number().min(0, 'Reorder point must be 0 or greater'),
  minimum_across_warehouses: yup.number().min(0, 'Minimum must be 0 or greater'),
  minimum_per_warehouses: yup.number().min(0, 'Minimum must be 0 or greater'),
  reorder_quantity: yup.number().min(1, 'Reorder quantity must be at least 1'),
  related_products: yup.array().of(
    yup.object().shape({
      id: yup.string(),
      name: yup.string()
    })
  ),
  media_url: yup
  .string()
  .test('is-valid-url', 'Media URL must be a valid URL', (value) => {
    if (!value) return true; // skip empty strings, use .required() if needed
    try {
      const url = new URL(value);
      return ['http:', 'https:'].includes(url.protocol);
    } catch (err) {
      return false;
    }
  }),
  images: yup.array().of(yup.mixed()),
  regular_price: yup.number(),
  member_price: yup.number(),
  average_cost: yup.number(),
  is_inventory_item: yup.boolean(),
  is_pays_commission: yup.boolean(),
  fixed_commission: yup.number(),
  percentage_commission: yup.number(),
  is_pays_bonus: yup.boolean(),
  fixed_bonus: yup.number(),
  percentage_bonus: yup.number(),
  pays_hours: yup.string(),
  purchase_description: yup.string(),
  primary_vendor_id: yup.string(),
  primary_vendor_purchase_price: yup.number(),
  secondary_vendor_id: yup.string(),
  secondary_vendor_purchase_price: yup.number(),
  tertiary_vendor_id: yup.string(),
  tertiary_vendor_purchase_price: yup.number()
});

const ProductForm = () => {
  const getRoutePath = useRoutePath();
  const { addProduct, updateProduct, getProductById, getProductAdditionalData,getProducts } = useProducts();
  const { getProductCategories } = useProductCategories();
  const { getEquipmentManufacturers } = useEquipmentManufacturers();
  const { getEquipmentTypes } = useEquipmentTypes();
  const { getVendors } = useVendors();
  const { id } = useParams();
  const navigate = useNavigate();
  const { showLoading, hideLoading } = useLoading();
  const isEdit = !!id;
  const [imagePreviews, setImagePreviews] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [showWarehousePopup, setShowWarehousePopup] = useState(false);
  const [warehouseROPs, setWarehouseROPs] = useState({});
  const [productCategories, setProductCategories] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [extWarrantyProviders, setExtWarrantyProviders] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const prodCatRef = useRef(null);
  const manuRef = useRef(null);
  const equipRef = useRef(null);
  const vendorRef = useRef(null);
  const prodRef = useRef(null);

  const getDefaultValues = useCallback((existingData = null) => {
    const data = existingData || {};
     
    return {
      name: data.name || '',
      product_category_id: data.product_category_id || '',
      description: data.description || '',
      active: data.active ?? true,
      is_equipment_item: data.is_equipment_item || false,
      manufacturer_id: data.manufacturer_id || '',
      equipment_type_id: data.equipment_type_id || '',
      model: data.model || '',
      part_number: data.part_number || '',
      sku: data.sku || '',
      upc: data.upc || '',
      type: data.type || '',
      is_extended_warranty: data.is_extended_warranty || false,
      warranty_provider_id: data.warranty_provider_id || '',
      warranty_expires: data.warranty_expires || '',
      reorder_point: data.reorder_point || 0,
      minimum_across_warehouses: data.minimum_across_warehouses || 0,
      minimum_per_warehouses: data.minimum_per_warehouses || 0,
      reorder_quantity: data.reorder_quantity || 1,
      related_products: data.related_products || [],
      media_url: data.media_url || '',
      images: data.display_images || [],
      regular_price: data.regular_price || 0,
      member_price: data.member_price || 0,
      average_cost: data.average_cost || 0,
      is_inventory_item: data.is_inventory_item || false,
      is_pays_commission: data.is_pays_commission || false,
      fixed_commission: data.fixed_commission || 0,
      percentage_commission: data.percentage_commission || 0,
      is_pays_bonus: data.is_pays_bonus || false,
      fixed_bonus: data.fixed_bonus || 0,
      percentage_bonus: data.percentage_bonus || 0,
      pays_hours: data.pays_hours || '00:00',
      purchase_description: data.purchase_description || '',
      primary_vendor_id: data.primary_vendor_id || '',
      primary_vendor_purchase_price: data.primary_vendor_purchase_price || 0,
      secondary_vendor_id: data.secondary_vendor_id || '',
      secondary_vendor_purchase_price: data.secondary_vendor_purchase_price || 0,
      tertiary_vendor_id: data.tertiary_vendor_id || '',
      tertiary_vendor_purchase_price: data.tertiary_vendor_purchase_price || 0,
      created_at: data.created_at || new Date().toISOString()
    };
  }, []);

  const [getEditData, setGetEditData] = useState(null);
  const hasFetched = useRef(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    setError,
    reset,
    trigger
  } = useForm({
    resolver: yupResolver(productSchema),
    defaultValues: getDefaultValues(getEditData),
    mode: 'onChange'
  });
  
useEffect(() => {
  const fetchProductAdditionalData = async () => {
    const productAdditionalData = await getProductAdditionalData();
   
    if(productAdditionalData?.extWarrantyProviders){
      setExtWarrantyProviders(productAdditionalData?.extWarrantyProviders);
    }
  };
  fetchProductAdditionalData()
}, [getProductAdditionalData]);

useEffect(() =>{
  const fetchProducts = async () => {
    const products = await getProducts(0,{all:true});
    if(products){
      setRelatedProducts(products);
    }
  };
  if(prodRef.current === null){
    fetchProducts();
    prodRef.current = true;
  }
 
}, [getProducts]);

useEffect(() => {
  const fetchVendors = async () => {
    const vendors = await getVendors(1,{all:true});
    if(vendors){
      setVendors(vendors);
    }
  };
  if(vendorRef.current === null){
    fetchVendors();
    vendorRef.current = true;
  }
}, [getVendors]);

useEffect(() => {
  const fetchManufacturers = async () => {
    
      const manufacturers = await getEquipmentManufacturers(1,{all:true});
      if(manufacturers){
        setManufacturers(manufacturers);
      }
       
  };
  const fetchEquipmentTypes = async () => {
    
    const equipmentTypes = await getEquipmentTypes(1,{all:true});
      if(equipmentTypes){
        setEquipmentTypes(equipmentTypes);
      }
      
  };

  if(manuRef.current === null){
    fetchManufacturers();
    manuRef.current = true;
  }
  if(equipRef.current === null){
    fetchEquipmentTypes();
    equipRef.current = true;
  }
  
  
}, [getEquipmentManufacturers, getEquipmentTypes]);

  useEffect(() => {
    const fetchProductCategories = async () => {
      const productCategories = await getProductCategories(0,{all:true});
      if(productCategories){
        setProductCategories(productCategories);
      }
    };
    if(prodCatRef.current === null){
      fetchProductCategories();
      prodCatRef.current = true;
    }
  }, [getProductCategories]);

  useEffect(() => {
    if(!id){
      reset(getDefaultValues()); 
      setImagePreviews([])
    }
  }, [id, reset, getDefaultValues]);

  const fetchProductData = useCallback(async () => {
    if (id && !hasFetched.current) {
      hasFetched.current = true;
      showLoading();
      try {
        const response = await getProductById(id);
        if (response?.data) {
          setGetEditData(response.data);
          // Reset form with fetched data
          reset(getDefaultValues(response.data));
          // Set image previews if any
          if (response.data.display_images && response.data.display_images.length > 0) {
            setImagePreviews(response.data.display_images);
          }
        }
      } catch (error) {
        console.error('Error fetching product data:', error);
        toast.error('Error fetching product data');
      } finally {
        hideLoading();
      }
    }
  }, [id, getProductById, showLoading, hideLoading, reset, getDefaultValues]);

  useEffect(() => {
    fetchProductData();
  }, [fetchProductData]);

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);

    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp', 'image/svg+xml'];
    const maxSize = 10 * 1024 * 1024; // 10MB

    const validFiles = files.filter(file => {
      if (!allowedTypes.includes(file.type)) {
        alert(`${file.name} has an invalid file type.`);
        return false;
      }
      if (file.size > maxSize) {
        alert(`${file.name} exceeds the 10MB size limit.`);
        return false;
      }
      return true;
    });
 
    if(validFiles.length === 0){
      return;
    }

    const newPreviews = files.map(file => ({
      id: Date.now() + Math.random(),
      file: file,
      name: file.name,
      size: file.size,
      type: file.type,
      created_at: new Date().toISOString(),
      url: URL.createObjectURL(file)
    }));
 
    setImagePreviews(prev => [...prev, ...newPreviews]);
  };

  const removeImage = (index) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'This image will be removed from the product',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, remove it!',
      cancelButtonText: 'Cancel'
    }).then((willDelete) => {
      
      if (willDelete.isConfirmed === true) {
        const currentRemoveIds = watch('image_removed_ids') || []; // Fallback to empty array
        setImagePreviews(prev => prev.filter((_, i) => i !== index));
        setValue('image_removed_ids', [...currentRemoveIds, imagePreviews[index].id]);
      }
    });
  };

  const addRelatedProduct = () => {
    const current = watch('related_products');
    setValue('related_products', [...current, { id: ''}]);
  };

  const removeRelatedProduct = (index) => {
    const current = watch('related_products');
    setValue('related_products', current.filter((_, i) => i !== index));
  };

  const getTabFields = (tabId) => {
    switch (tabId) {
      case 'details':
        const detailsFields = ['name','product_category_id'];
        // Add conditional required fields
     
        if (watch('is_equipment_item')) {
          detailsFields.push('manufacturer_id', 'equipment_type_id');
        }
        
        if (watch('is_extended_warranty')) {
          detailsFields.push('warranty_provider_id', 'warranty_expires');
        }
        return detailsFields;
      case 'pricing':
        const pricingFields = [];
        // Add conditional required fields for inventory items
        if (watch('is_inventory_item')) {
          pricingFields.push('reorder_point', 'minimum_across_warehouses', 'minimum_per_warehouses', 'reorder_quantity');
        }
        return pricingFields;
      case 'commission':
        return [];
      case 'vendors':
        return [];
      case 'media':
        return [];
      default:
        return [];
    }
  };

  const handleTabChange = (tabId) => {
    // Get required fields for current tab
    const currentTabFields = getTabFields(activeTab);
    
    // Check all required fields
    const emptyFields = currentTabFields.filter(field => {
      let value = watch(field);
      
      // Handle nested fields
      if (field.includes('.')) {
        const [, child] = field.split('.');
        value = watch(field)?.[child];
      }
      
      // Handle falsy values like "", null, undefined, empty arrays
      if (Array.isArray(value)) {
        return value.length === 0;
      }
      
      return value === undefined || value === null || value === '';
    });
   
    if (emptyFields.length > 0) {
      // Set errors for all empty fields
      emptyFields.forEach(field => {
        setError(field, {
          type: 'required',
          message: `${getFieldDisplayName(field)} is required`
        });
      });
     
      // Show error toast with all missing fields
      const fieldNames = emptyFields.map(field => getFieldDisplayName(field)).join(', ');
      toast.error(`Please fill in the following required fields: ${fieldNames}`);
      return;
    }

    // If all required fields are filled, allow tab change
    setActiveTab(tabId);
  };

  const getFieldDisplayName = (field) => {
    switch(field) {
      case 'name': return 'Product Name';
      case 'product_category_id': return 'Product Category';
      case 'manufacturer_id': return 'Manufacturer';
      case 'equipment_type_id': return 'Equipment Type';
      case 'warranty_provider_id': return 'Warranty Provider';
      case 'warranty_expires': return 'Warranty Expires';
      case 'reorder_point': return 'Reorder Point';
      case 'minimum_across_warehouses': return 'Minimum Across Warehouses';
      case 'minimum_per_warehouses': return 'Minimum Per Warehouse';
      case 'reorder_quantity': return 'Reorder Quantity';
      default: return field;
    }
  };

  const onSubmit = async (data) => {
    
    showLoading();
    setIsLoading(true);
    
    try {
      const productData = {
        ...data,
        images: imagePreviews
      };

      if (id) {
        const response = await updateProduct(id, productData);
        if (response.status === 200) {
          toast.success('Product updated successfully!');
          setIsLoading(false);
          navigate(getRoutePath('/products'));
        } else {
          handleErrorResponse(response);
        }
      } else {
        const response = await addProduct(productData);
        if (response.status === 200) {
          toast.success('Product added successfully!');
          setIsLoading(false);
          navigate(getRoutePath('/products'));
        } else {
          handleErrorResponse(response);
        }
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('An error occurred while saving the product');
      setIsLoading(false);
    } finally {
      hideLoading();
      setIsLoading(false);
    }
  };

  const handleErrorResponse = (response) => {
    setIsLoading(false);
   
    if (response.errors) {
            
      Object.entries(response.errors).forEach(([field, message]) => {
        // Extract actual message
        let errorMessage = '';

        if (Array.isArray(message)) {
          errorMessage = message[0]; // handle array case
        } else if (typeof message === 'object' && message !== null) {
          errorMessage = Object.values(message)[0]; // take the first value from the object
        } else {
          errorMessage = String(message); // fallback
        }

        setError(field, {
          type: 'manual',
          message: errorMessage,
        });

      
        toast.error(`${field}: ${errorMessage}`);
        
      });
    } else if (response.error?.message) {
      setError('general', {
        type: 'manual',
        message: response.error.message,
      });
    
    }
  };

  const tabs = [
    { id: 'details', label: 'Product Details' },
    { id: 'pricing', label: 'Pricing & Inventory' },
    { id: 'commission', label: 'Commission Settings' },
    { id: 'vendors', label: 'Vendor Information' },
    { id: 'media', label: 'Media & Related' }
  ];

  return (
    <div className="min-h-screen ">
      <div className="max-w-10xl mx-auto px-4 sm:px-6 md:px-8">
        <Breadcrumb />
      <div className="bg-white rounded-lg shadow-md p-6" bis_skin_checked="1">  
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            {isEdit ? 'Edit Product' : 'Create New Product'}
          </h1>
          <p className="mt-2 text-gray-600">
            {isEdit ? 'Update product information and settings' : 'Add a new product to your inventory'}
          </p>
        </div>
       
        {/* Navigation Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 overflow-x-auto">
            {tabs.map((tab) => (
                              <button
                  key={tab.id}
                  type="button"
                  onClick={() => handleTabChange(tab.id)}
                  className={`
                    whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                    ${activeTab === tab.id
                      ? 'border-[#009efb] text-[#009efb]'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  {tab.label}
                </button>
            ))}
          </nav>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          {/* Product Details Section */}
          {activeTab === 'details' && (
            <ProductDetailsTab
              register={register}
              errors={errors}
              watch={watch}
              setValue={setValue}
              inputClassName="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm"
              productCategories={productCategories}
              manufacturers={manufacturers}
              equipmentTypes={equipmentTypes}
              extWarrantyProviders={extWarrantyProviders}
              trigger={trigger}
            />
          )}

          {/* Pricing & Inventory Section */}
          {activeTab === 'pricing' && (
            <PricingInventoryTab
              register={register}
              errors={errors}
              watch={watch}
              setValue={setValue}
              inputClassName="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm"
              setShowWarehousePopup={setShowWarehousePopup}
            />
          )}

          {/* Commission Settings Section */}
          {activeTab === 'commission' && (
            <CommissionSettingsTab
              register={register}
              errors={errors}
              watch={watch}
              setValue={setValue}
              inputClassName="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm"
            />
          )}

          {/* Vendor Information Section */}
          {activeTab === 'vendors' && (
            <VendorInformationTab
              register={register}
              errors={errors}
              watch={watch}
              setValue={setValue}
              vendors={vendors}
              inputClassName="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm"
            />
          )}

          {/* Media & Related Section */}
          {activeTab === 'media' && (
            <MediaRelatedTab
              register={register}
              errors={errors}
              watch={watch}
              setValue={setValue}
              inputClassName="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm"
              imagePreviews={imagePreviews}
              handleImageChange={handleImageChange}
              removeImage={removeImage}
              addRelatedProduct={addRelatedProduct}
              removeRelatedProduct={removeRelatedProduct}
              relatedProducts={relatedProducts}
            />
          )}

          {/* Navigation buttons */}
          <div className="flex justify-center sm:justify-between md:justify-end mt-6">
            <div className="flex space-x-4">
              {activeTab !== 'details' && (
                <button
                  type="button"
                  onClick={() => handleTabChange(tabs[tabs.findIndex(t => t.id === activeTab) - 1].id)}
                  className="flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <ArrowLeftIcon className="w-4 h-4 mr-2" />
                  Previous
                </button>
              )}
              {activeTab !== 'media' ? (
                <button
                  type="button"
                  onClick={() => handleTabChange(tabs[tabs.findIndex(t => t.id === activeTab) + 1].id)}
                  className="flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#009efb] hover:bg-[#0089db]"
                >
                  Next
                  <ArrowRightIcon className="w-4 h-4 ml-2" />
                </button>
              ) : (
                isLoading ? (
                  <div className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#009efb] hover:bg-[#0089db]">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  </div>
                ) : (
                  <button
                    type="submit"
                    className="flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#009efb] hover:bg-[#0089db]"
                  >
                    <CogIcon className="w-4 h-4 mr-2" />
                    {isEdit ? 'Update Product' : 'Create Product'}
                  </button>
                )
              )}
            </div>
          </div>
        </form>

        {/* Warehouse ROP Popup Modal */}
        {showWarehousePopup && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Set Individual Warehouse ROP's</h3>
                  <button
                    onClick={() => setShowWarehousePopup(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              
              <div className="p-6 space-y-4">
                <p className="text-sm text-gray-600 mb-4">
                  Set individual reorder points for each warehouse location.
                </p>
                
                {/* Sample warehouses - replace with actual warehouse data */}
                {[
                  { id: 'warehouse1', name: 'Main Warehouse', location: 'New York' },
                  { id: 'warehouse2', name: 'West Coast Hub', location: 'Los Angeles' },
                  { id: 'warehouse3', name: 'South Distribution', location: 'Houston' },
                  { id: 'warehouse4', name: 'North Storage', location: 'Chicago' }
                ].map((warehouse) => (
                  <div key={warehouse.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div>
                      <h4 className="font-medium text-gray-900">{warehouse.name}</h4>
                      <p className="text-sm text-gray-500">{warehouse.location}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <label className="text-sm font-medium text-gray-700">ROP:</label>
                      <input
                        type="number"
                        min="0"
                        value={warehouseROPs[warehouse.id] || ''}
                        onChange={(e) => setWarehouseROPs(prev => ({
                          ...prev,
                          [warehouse.id]: e.target.value
                        }))}
                        className="w-20 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb]"
                        placeholder="0"
                      />
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
                <button
                  onClick={() => setShowWarehousePopup(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb] transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    // Here you can save the warehouse ROPs
                    console.log('Warehouse ROPs:', warehouseROPs);
                    setShowWarehousePopup(false);
                  }}
                  className="px-4 py-2 bg-[#009efb] text-white rounded-md hover:bg-[#0089db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb] transition-colors"
                >
                  Save ROP's
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default ProductForm; 